import React, { useEffect, useRef, useState } from 'react';

import { MainPrescriptionCode } from '../../../../Common/Pim/Enums/MainPrescriptionCode.csharp';
import { getTranslation, keys } from '../../../../Resources/Translations';
import { ProductCard } from '../../../Partials/ProductCard/ProductCard';
import { ProductCardPlaceholder } from '../../../Partials/ProductCard/ProductCardPlaceholder/ProductCardPlaceholder';
import { ProductCardVariant } from '../../../Partials/ProductCard/ProductCardVariant.csharp';
import { ProductCardViewModel } from '../../../Partials/ProductCard/ProductCardViewModel.csharp';
import { handleProductCardRequest } from '../../../Purchase/Cart/Components/CartUtils';
import { post, postFor } from '../../../ReactComponents/API';
import { ApplicationInsights, SeverityLevel } from '../../../ReactComponents/ApplicationInsights';
import { CartDrawerRecommendationProps } from '../../Blocks/CartDrawerRecommendationBlock/CartDrawerRecommendationProps.csharp';
import { Recommendation } from '../../ViewModels/Recommendation.csharp';
import { RecommendationsCache } from '../../ViewModels/RecommendationsCache';
import { RecommendationsEventNameResolver } from '../../ViewModels/RecommendationsEventNameResolver';
import { RecommendedProductCardsRequest } from '../../ViewModels/RecommendedProductCardsRequest.csharp';

export const CartDrawerRecommendedProducts = (props: CartDrawerRecommendationProps) => {
    const [productList, setProductList] = useState<ProductCardViewModel[]>([]);
    const [product, setProduct] = useState<ProductCardViewModel>();
    const [isLoading, setIsLoading] = useState(true);
    const eventWasFired = useRef(false);
    const RecommendationsTimeoutMs = 500;
    const TimeoutLoadProducts = 1500;
    const amountLeftForFreeDelivery =
        props.shippingInfo && props.shippingInfo.cheapestMethodInfo && props.shippingInfo.cheapestMethodInfo.amountLeftForFreeDelivery;

    useEffect(() => {
        window.addEventListener(RecommendationsEventNameResolver.GetEventName(props.model.area), loadProductsWithTimeout);

        setTimeout(() => {
            if (!eventWasFired.current && RecommendationsCache.getRecommendations(props.model.area)) {
                loadProducts();
            }
        }, TimeoutLoadProducts);

        return () => {
            window.removeEventListener(RecommendationsEventNameResolver.GetEventName(props.model.area), loadProductsWithTimeout);
        };
    }, []);

    useEffect(() => {
        if (product) {
            validateProduct(product);
        }
    }, [product]);

    useEffect(() => {
        if (amountLeftForFreeDelivery && amountLeftForFreeDelivery > 0 && productList.some((p) => p.placedPrice >= amountLeftForFreeDelivery)) {
            setProduct(productList.filter((p) => p.placedPrice >= amountLeftForFreeDelivery).find((p) => p));
        } else {
            setProduct(productList[0]);
        }
    }, [productList]);

    const loadProductsWithTimeout = () => {
        eventWasFired.current = true;
        setTimeout(loadProducts, RecommendationsTimeoutMs);
    };

    const loadProducts = () => {
        setIsLoading(true);
        let recommendations: Recommendation[] = [];
        try {
            recommendations = JSON.parse(RecommendationsCache.getRecommendations(props.model.area)!) as Recommendation[];
        } catch (err) {
            ApplicationInsights.TrackTrace(
                SeverityLevel.Error,
                `Failed to parse JSON in loadProducts. Data: ${RecommendationsCache.getRecommendations(props.model.area)} Error: ${err}`
            );
        }
        getProductCards(recommendations);
    };

    const getProductCards = (recs: Recommendation[]) => {
        const recommendations = [...(recs || [])];
        const request = {
            settings: {
                includeOutOfStockItems: false,
                originName: recs?.length > 0 ? props.model.originName : props.model.fallbackName
            },
            recommendations: recommendations
        } as RecommendedProductCardsRequest;

        postFor<ProductCardViewModel[]>(`${props.productsUrl}`, request).then((productList) => {
            if (props.model.excludeOtcProducts) {
                setProductList(productList.filter((p) => p.prescriptionCode !== MainPrescriptionCode.OTC));
            } else {
                setProductList(productList);
            }
        });
    };

    const validateProduct = (productCardViewModel: ProductCardViewModel) => {
        const productCode = productCardViewModel.code;
        const url = props.validateItemUrl.transformUrl({ productCode });
        handleProductCardRequest(post(url))
            .then(() => setTimeout(() => setIsLoading(false), RecommendationsTimeoutMs))
            .catch(() => {
                const index = productList.indexOf(product!);
                if (index > -1) {
                    setProductList(productList.splice(index, 1));
                }
            });
    };

    return (
        <>
            {product && (
                <div className="CartDrawerRecommendedProducts">
                    {amountLeftForFreeDelivery && amountLeftForFreeDelivery > 0 && product.placedPrice >= amountLeftForFreeDelivery ? (
                        <div className="CartDrawerRecommendedProducts_CompletePurchase">
                            {getTranslation(keys['cartSummary.completePurchaseForFreeDelivery'])}
                        </div>
                    ) : (
                        <div className="CartDrawerRecommendedProducts_CompletePurchase">{getTranslation(keys['cartSummary.completePurchase'])}</div>
                    )}

                    {isLoading ? (
                        <ProductCardPlaceholder variant={ProductCardVariant.HorizontalCompact} />
                    ) : (
                        <ProductCard
                            profile={props.profile}
                            cartItemUrl={props.cartItemUrl}
                            validateItemUrl={props.validateItemUrl}
                            experimentProductClickUrl={props.experimentProductClickUrl}
                            showFavouritesButton={false}
                            epiFindClickTrackingUrl={props.epiFindClickTrackingUrl}
                            model={product!}
                            hidePricePerUnit
                            isLazyLoading
                            rxUrl={''}
                            variant={ProductCardVariant.HorizontalCompact}
                            hidePowerReviews={product.hidePowerReviews}
                            disableQuantityPicker
                        />
                    )}
                </div>
            )}
        </>
    );
};
